/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ZoneDetails.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Typography } from "@mui/material";

import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";

import { HandleApiActions } from "../../redux/actions/actionHandler";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import settingsApis from "../../redux/actions/settings";
import carForVLC from "../../assets/carForVLC.png";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

function ZoneDetails({ zoneDetails }) {
  const dispatch = useDispatch();
  const appMetaZoneDetails = useSelector((state) => state?.settingsReducer);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [zonePositions, setZonePositions] = useState([]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...settingsApis.get_app_meta_data,
        show_toast: false,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (appMetaZoneDetails?.metaData?.zone_positions) {
      let availableZones = appMetaZoneDetails?.metaData?.zone_positions;
      if (zoneDetails?.length) {
        const existingZones = zoneDetails?.map((data) => data.zone_position);
        availableZones = appMetaZoneDetails?.metaData?.zone_positions.filter(
          (data) => !existingZones.includes(data),
        );
      }
      setZonePositions(availableZones);
    }
  }, [appMetaZoneDetails, zoneDetails]);

  const handleAddClick = () => {
    setOpenAddDialog(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: {
            xs: "0 8px 2px 0px",
            md: "0 12px 4px 0px",
          },
          flexShrink: 0,
          gap: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            fontStyle: "Open Sans",
            color: "#53565A",
          }}
        >
          Zone
        </Typography>
        <Box>
          <AddButtonV2 handleOnClick={handleAddClick} />
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: { xs: "8px", md: "16px" },
          minHeight: "400px",
          mt: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "420px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              width: "90%",
              gap: 1,
            }}
          >
            {zoneDetails.find((z) => z.zone_position === "FRONT-LEFT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "FRONT-LEFT")
                    .position_name
                }
              </Box>
            )}

            {zoneDetails.find((z) => z.zone_position === "FRONT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "FRONT")
                    .position_name
                }
              </Box>
            )}

            {zoneDetails.find((z) => z.zone_position === "FRONT-RIGHT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "FRONT-RIGHT")
                    .position_name
                }
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            {zoneDetails.find((z) => z.zone_position === "LEFT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "LEFT")
                    .position_name
                }
              </Box>
            )}

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "250px",
              }}
            >
              <img
                src={carForVLC}
                alt="Vehicle"
                style={{
                  width: "100%",
                  maxWidth: "200px",
                  height: "auto",
                  objectFit: "contain",
                  opacity: 0.9,
                }}
              />
            </Box>

            {zoneDetails.find((z) => z.zone_position === "RIGHT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "RIGHT")
                    .position_name
                }
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              width: "90%",
              gap: 1,
            }}
          >
            {zoneDetails.find((z) => z.zone_position === "REAR-LEFT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "REAR-LEFT")
                    .position_name
                }
              </Box>
            )}

            {zoneDetails.find((z) => z.zone_position === "REAR") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "REAR")
                    .position_name
                }
              </Box>
            )}

            {zoneDetails.find((z) => z.zone_position === "REAR-RIGHT") && (
              <Box
                sx={{
                  backgroundColor: "#26890D",
                  color: "#FFFFFF",
                  padding: "8px 12px",
                  borderRadius: "3px",
                  fontStyle: "Open Sans",
                  fontSize: "11px",
                  textAlign: "center",
                  minWidth: "70px",
                  opacity: 0.9,
                }}
              >
                {
                  zoneDetails.find((z) => z.zone_position === "REAR-RIGHT")
                    .position_name
                }
              </Box>
            )}
          </Box>

          {zoneDetails.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#9CA3AF",
                  fontFamily: "Open Sans",
                }}
              >
                No zones configured. Click EDIT to add zones.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <AddZoneDetails
        open={openAddDialog}
        setOpenAddDialog={setOpenAddDialog}
        zonePositions={zonePositions}
      />
    </Box>
  );
}

export default ZoneDetails;

ZoneDetails.propTypes = {
  zoneDetails: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      position_name: PropTypes.string,
      zone_position: PropTypes.string,
    }),
  ),
};

const AddZoneDetails = ({ open, setOpenAddDialog, zonePositions }) => {
  const dispatch = useDispatch();
  const [zoneName, setZoneName] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");

  const handleClose = () => {
    setOpenAddDialog(false);
    setZoneName("");
    setSelectedPosition("");
  };

  const handleAdd = () => {
    const reqObj = {
      position_name: zoneName,
      zone_position: selectedPosition,
      current_time: new Date().toISOString(),
    };

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.add_zone_details,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_admin_settings,
          show_toast: false,
        }),
      );
    });

    handleClose();
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title="Add Zone"
      successButtonName="Add"
      rejectButtonName="Close"
      onClickSuccess={handleAdd}
      onDisabled={
        !(zoneName?.length > 2 && zoneName?.trim() !== "" && selectedPosition)
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Zone Name"
          value={zoneName}
          setValue={(val) => {
            if (val.length <= 35) {
              setZoneName(val);
            }
          }}
          required
          maxLength={35}
        />
        {zonePositions?.filter(Boolean)?.length > 0 ? (
          <CustomSelectV2
            size="small"
            label="Select Zone Position"
            required
            optionsList={
              zonePositions?.filter(Boolean)?.map((el) => ({
                value: el,
                label: el,
              })) || []
            }
            selectedValue={selectedPosition || " "}
            handleOnChange={(e) => {
              setSelectedPosition(e.target.value);
            }}
          />
        ) : (
          <CustomSelectV2
            size="small"
            label="Select Zone Position"
            required
            optionsList={[
              {
                value: "no_data",
                label: "No data",
              },
            ]}
            selectedValue="no_data"
            disabled
          />
        )}
      </Stack>
    </CustomDialogBoxV2>
  );
};

AddZoneDetails.propTypes = {
  open: PropTypes.bool,
  setOpenAddDialog: PropTypes.func,
  zonePositions: PropTypes.arrayOf(PropTypes.string),
};
